import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { Fade } from '../../helpers';
import { ActionCell, ArrowLeft, ArrowRight, BodyCell, BodyRow, EmptyMessage, HeaderCell, HeaderRow, LoadingOverlay, } from './_elements';
import { TableVariant } from './types';
import { useColumnLayout } from './_hooks/useColumnLayout';
import { useRowLayout } from './_hooks/useRowLayout';
import { useHorizontalScroll } from './_hooks/useHorizontalScroll';
import { TableProvider } from './_context/TableProvider';
import { getColumnDef, getColumnMeta } from './utils';
import { Pagination } from './footer/Pagination';
const Wrapper = styled.div `
  --sticky-left-show: ${({ $isLeft, $showScrollHint }) => ($showScrollHint && !$isLeft ? 1 : 0)};
  --sticky-right-show: ${({ $isRight, $showScrollHint }) => ($showScrollHint && !$isRight ? 1 : 0)};

  ${({ $stickyLeftWidth, $stickyRightWidth }) => css `
    --sticky-left-offset: ${$stickyLeftWidth + 1}px;
    --sticky-right-offset: ${$stickyRightWidth - 1}px;
  `}

  display: grid;
  position: relative;
  max-width: 100%;
`;
const TableContainer = styled.div `
  width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  border: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
  border-radius: 8px;

  ${({ $variant }) => $variant === TableVariant.Grid &&
    css `
      border-radius: 8px;
    `}
`;
const TableWrapper = styled.table `
  position: relative;
  display: grid;
  grid-template-columns: ${({ $columnsStyle }) => $columnsStyle};
  width: 100%;
  min-width: fit-content;
  transition: height 0.2s ease;

  ${({ $fixedHeight }) => $fixedHeight !== undefined &&
    css `
      height: ${$fixedHeight}px;
    `}
`;
const StyledThead = styled.thead `
  display: grid;
  grid-auto-rows: min-content;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
`;
const StyledTbody = styled.tbody `
  display: grid;
  grid-auto-rows: min-content;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
`;
export const Table = ({ columns: columnsProp, columnVisibility, data, isLoading, getRowId, headerAction, onRowClick, rowAction, emptyMessage = 'No entries found', pagination, variant, }) => {
    const scrollContainerRef = useRef(null);
    const tableWrapperRef = useRef(null);
    const headerRef = useRef(null);
    const rowsRef = useRef(null);
    const [isInitialLoad, setIsInitialLoad] = useState(isLoading && data.length === 0);
    useEffect(() => {
        if (!isLoading && data.length > 0) {
            setIsInitialLoad(false);
        }
    }, [data, isLoading]);
    const columns = useMemo(() => {
        return columnsProp.map(getColumnDef);
    }, [columnsProp]);
    const table = useReactTable({
        autoResetAll: false,
        data,
        columns,
        enableRowSelection: false,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
        getRowId,
        manualPagination: pagination ? true : false,
        state: {
            columnVisibility,
            pagination,
        },
    });
    const { tableHeight, headerHeight } = useRowLayout({
        isLoading,
        isPaginationEnabled: !!pagination,
        headerRef,
        tableRef: tableWrapperRef,
        rowCount: data.length,
        rowsRef,
    });
    const { columnsStyle, stickyLeftPositions, stickyRightPositions, stickyLeftWidth, stickyRightWidth } = useColumnLayout(table.getVisibleLeafColumns(), {
        isLoading,
        tableRef: tableWrapperRef,
        headerAction,
        rowAction,
    });
    const { isAtLeft, isAtRight, showScrollHint, onScroll, onMouseEnter } = useHorizontalScroll(scrollContainerRef);
    return (React.createElement("div", null,
        React.createElement(TableProvider, { value: {
                columns,
            } },
            React.createElement(Wrapper, { onMouseEnter: onMouseEnter, "$stickyLeftWidth": stickyLeftWidth, "$stickyRightWidth": stickyRightWidth, "$isLeft": isAtLeft, "$isRight": isAtRight, "$showScrollHint": showScrollHint, "$variant": variant },
                table.getRowModel().rows.length > 0 && !isLoading && (React.createElement(React.Fragment, null,
                    React.createElement(ArrowLeft, null),
                    React.createElement(ArrowRight, null))),
                React.createElement(LoadingOverlay, { isVisible: !!isLoading, topOffset: headerHeight }),
                React.createElement(TableContainer, { "$variant": variant, onScroll: onScroll, ref: scrollContainerRef, "data-role": "table-container" },
                    React.createElement(TableWrapper, { ref: tableWrapperRef, "$columnsStyle": columnsStyle, "$fixedHeight": Math.max(headerHeight, tableHeight ?? 0) },
                        React.createElement(StyledThead, null,
                            React.createElement(HeaderRow, { "data-role": "table-header-row", "$variant": variant, ref: headerRef },
                                table.getHeaderGroups().map((headerGroup) => {
                                    return headerGroup.headers.map((header) => {
                                        const meta = getColumnMeta(header.column);
                                        return (React.createElement(HeaderCell, { "data-role": "table-header-cell", "$isStickyLeft": meta.sticky?.enabled && meta.sticky?.side !== 'right', "$stickyLeft": stickyLeftPositions[header.index], "$isStickyRight": meta.sticky?.enabled && meta.sticky?.side === 'right', "$stickyRight": stickyRightPositions[header.index], "$hasAction": !!rowAction || !!headerAction, "$variant": variant, key: header.id }, flexRender(header.column.columnDef.header, header.getContext())));
                                    });
                                }),
                                (rowAction || headerAction) && (React.createElement(ActionCell, { isHeader: true, isEmpty: !headerAction, variant: variant }, headerAction)))),
                        React.createElement(StyledTbody, null, table.getRowModel().rows.map((row, rowIndex) => (React.createElement(BodyRow, { "data-role": "table-body-row", ref: rowIndex === 0 ? rowsRef : undefined, key: row.id, onClick: onRowClick ? () => onRowClick(row.original) : undefined, "$isClickable": !!onRowClick, "$variant": variant },
                            row.getVisibleCells().map((cell, cellIndex) => {
                                const meta = getColumnMeta(cell.column);
                                return (React.createElement(BodyCell, { "data-role": "table-body-cell", "$isStickyLeft": meta.sticky?.enabled && meta.sticky?.side !== 'right', "$stickyLeft": stickyLeftPositions[cellIndex], "$isStickyRight": meta.sticky?.enabled && meta.sticky?.side === 'right', "$stickyRight": stickyRightPositions[cellIndex], "$hasAction": !!rowAction || !!headerAction, "$variant": variant, key: `${row.id}-${cell.column.id}` }, flexRender(cell.column.columnDef.cell, cell.getContext())));
                            }),
                            rowAction && React.createElement(ActionCell, { variant: variant }, rowAction(row.original, rowIndex))))))),
                    table.getRowModel().rows.length === 0 && !isLoading && React.createElement(EmptyMessage, { message: emptyMessage }))),
            pagination && (React.createElement(Fade, { show: !isInitialLoad },
                React.createElement(Pagination, { ...pagination }))))));
};
//# sourceMappingURL=Table.js.map